import React from "react";
import { ArrowUpRight, ArrowDownRight, Lock, CheckCircle2 } from "lucide-react";
import { fmtPrice, ZoneBadge } from "./ZoneVisualizer";
import { computeProximity, proximityLabel, proximityColor } from "../lib/proximity";

export default function SetupCards({ setups, onSelect }) {
  if (!setups.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
      {setups.map(s => <SetupCard key={s.id} setup={s} onSelect={onSelect} />)}
    </div>
  );
}

function SetupCard({ setup: s, onSelect }) {
  const isLong = s.direction === "long";
  const DirIcon = isLong ? ArrowUpRight : ArrowDownRight;
  const dirColor = isLong ? "text-emerald-500" : "text-rose-500";
  const closed = s.status === "closed";
  const ch = s.day_change_percent;
  const chColor = ch == null ? "text-zinc-500" : ch > 0 ? "text-emerald-400" : ch < 0 ? "text-rose-400" : "text-zinc-400";
  const prox = computeProximity(s);
  const proxLbl = proximityLabel(prox);

  const entryZones = s.zones?.filter(z => z.type === "long_entry" || z.type === "short_entry") || [];
  const targets = s.zones?.filter(z => z.type === "target") || [];

  return (
    <button
      onClick={() => onSelect(s.id)}
      className="text-left border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 hover:border-zinc-700 transition-colors p-4 space-y-3 w-full"
      data-testid={`setup-card-${s.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <DirIcon size={14} className={`${dirColor} shrink-0`} strokeWidth={2.5} />
            <span className="font-display font-black text-base truncate">{s.asset_name}</span>
            {s.is_attractive_now && <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />}
            {closed && <Lock size={11} className="text-zinc-600 shrink-0" />}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono-num mt-0.5">{s.symbol}</div>
        </div>
        <span className={`px-1.5 py-0.5 text-[9px] font-display font-bold uppercase tracking-wider rounded-sm shrink-0 ${
          s.category === "Gold" ? "bg-yellow-500/15 text-yellow-400" :
          s.category === "Krypto" ? "bg-blue-500/15 text-blue-400" :
          s.category === "Aktien" ? "bg-purple-500/15 text-purple-400" :
          "bg-zinc-800 text-zinc-400"
        }`}>
          {s.category}
        </span>
      </div>

      {/* Price row */}
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono-num font-bold text-lg text-white leading-none">
            {s.last_price != null ? fmtPrice(s.last_price) : "—"}
          </div>
          <div className={`text-xs font-mono-num font-bold mt-0.5 ${chColor}`}>
            {ch != null ? `${ch > 0 ? "+" : ""}${ch.toFixed(2)}%` : "—"}
          </div>
        </div>
        {proxLbl && (
          <span className={`px-2 py-1 border text-[9px] font-display font-bold uppercase tracking-wider rounded-sm ${proximityColor(prox)}`}>
            {proxLbl}
          </span>
        )}
      </div>

      {/* Zones preview */}
      {entryZones.length > 0 && (
        <div className="space-y-1 pt-2 border-t border-zinc-900">
          {entryZones.slice(0, 2).map(z => (
            <div key={z.id} className="flex items-center justify-between gap-2 text-[11px]">
              <ZoneBadge zone={z} />
              <span className="font-mono-num text-zinc-400">
                {fmtPrice(z.price_min)} – {fmtPrice(z.price_max)}
              </span>
            </div>
          ))}
          {targets.length > 0 && (
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <ZoneBadge zone={targets[0]} />
              <span className="font-mono-num text-zinc-400">
                {fmtPrice(targets[0].price_min)} – {fmtPrice(targets[0].price_max)}
              </span>
            </div>
          )}
        </div>
      )}
    </button>
  );
}
